/**
 * Wallet Sync Service
 * Handles blockchain wallet integration and synchronization
 */

import { BrowserProvider, isAddress, formatEther } from 'ethers';
import apiDefaults from './api';
import { ApiResponse } from '../types';

export interface WalletInfo {
  address: string;
  chainId: number;
  chainName: string;
  balance: string;
  ensName?: string;
  isConnected: boolean;
  provider?: any;
}

export interface WalletTransaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  gasUsed: string;
  gasPrice: string;
  blockNumber: number;
  timestamp: number;
  status: 'pending' | 'confirmed' | 'failed';
  type: 'dividend_payout' | 'share_purchase' | 'share_sale' | 'staking_reward';
}

export interface BlockchainNetwork {
  chainId: number;
  name: string;
  rpcUrl: string;
  blockExplorerUrl: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  isTestnet: boolean;
}

// Supported networks
const SUPPORTED_NETWORKS: BlockchainNetwork[] = [
  {
    chainId: 1440002,
    name: 'XRP EVM Sidechain',
    rpcUrl: 'https://rpc-evm-sidechain.xrpl.org',
    blockExplorerUrl: 'https://evm-sidechain.xrpl.org',
    nativeCurrency: {
      name: 'XRP',
      symbol: 'XRP',
      decimals: 18,
    },
    isTestnet: false,
  },
  {
    chainId: 1,
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://mainnet.infura.io/v3/YOUR_INFURA_KEY',
    blockExplorerUrl: 'https://etherscan.io',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    isTestnet: false,
  },
  {
    chainId: 11155111,
    name: 'Ethereum Sepolia',
    rpcUrl: 'https://sepolia.infura.io/v3/YOUR_INFURA_KEY',
    blockExplorerUrl: 'https://sepolia.etherscan.io',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'SepoliaETH',
      decimals: 18,
    },
    isTestnet: true,
  },
];

class WalletService {
  private walletInfo: WalletInfo | null = null;
  private provider: BrowserProvider | null = null;

  /**
   * Check if MetaMask or other Web3 wallet is available
   */
  isWalletAvailable(): boolean {
    return typeof window !== 'undefined' && typeof (window as any).ethereum !== 'undefined';
  }

  /**
   * Connect to user's wallet
   */
  async connectWallet(): Promise<{ success: boolean; walletInfo?: WalletInfo; error?: string }> {
    try {
      if (!this.isWalletAvailable()) {
        return {
          success: false,
          error: 'No Web3 wallet detected. Please install MetaMask or another Web3 wallet.'
        };
      }

      // Request account access
      const ethereum = (window as any).ethereum;
      await ethereum.request({ method: 'eth_requestAccounts' });

      // Create provider
      this.provider = new BrowserProvider(ethereum);
      const signer = await this.provider.getSigner();
      const address = await signer.getAddress();

      // Get network info
      const network = await this.provider.getNetwork();
      const balance = await this.provider.getBalance(address);

      // Try to get ENS name
      let ensName: string | undefined;
      try {
        ensName = await this.provider.lookupAddress(address) || undefined;
      } catch (error) {
        // ENS not available on this network or other error
        console.debug('ENS lookup failed:', error);
      }

      // Get supported network info
      const chainId = Number(network.chainId);
      const supportedNetwork = SUPPORTED_NETWORKS.find(n => n.chainId === chainId);
      const chainName = supportedNetwork?.name || `Chain ${chainId}`;

      this.walletInfo = {
        address,
        chainId,
        chainName,
        balance: formatEther(balance),
        ensName,
        isConnected: true,
        provider: this.provider
      };

      // Sync wallet with backend
      await this.syncWalletWithBackend(address);

      // Set up event listeners
      this.setupWalletEventListeners();

      return {
        success: true,
        walletInfo: this.walletInfo || undefined
      };
    } catch (error: any) {
      console.error('Wallet connection error:', error);
      
      let errorMessage = 'Failed to connect wallet';
      if (error.code === 4001) {
        errorMessage = 'Wallet connection was rejected by user';
      } else if (error.code === -32002) {
        errorMessage = 'Wallet connection request is already pending';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Disconnect wallet
   */
  async disconnectWallet(): Promise<void> {
    this.walletInfo = null;
    this.provider = null;
    
    // Remove event listeners
    if (typeof window !== 'undefined' && (window as any).ethereum) {
      const ethereum = (window as any).ethereum;
      ethereum.removeAllListeners();
    }

    // Notify backend
    try {
      await apiDefaults.post('/wallet/disconnect');
    } catch (error) {
      console.error('Error notifying backend of wallet disconnect:', error);
    }
  }

  /**
   * Get current wallet info
   */
  getWalletInfo(): WalletInfo | null {
    return this.walletInfo;
  }

  /**
   * Switch to supported network
   */
  async switchNetwork(chainId: number): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.isWalletAvailable()) {
        return { success: false, error: 'No wallet available' };
      }

      const network = SUPPORTED_NETWORKS.find(n => n.chainId === chainId);
      if (!network) {
        return { success: false, error: 'Unsupported network' };
      }

      const ethereum = (window as any).ethereum;

      try {
        // Try to switch to the network
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${chainId.toString(16)}` }],
        });
      } catch (switchError: any) {
        // If network doesn't exist, add it
        if (switchError.code === 4902) {
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${chainId.toString(16)}`,
              chainName: network.name,
              rpcUrls: [network.rpcUrl],
              blockExplorerUrls: [network.blockExplorerUrl],
              nativeCurrency: network.nativeCurrency,
            }],
          });
        } else {
          throw switchError;
        }
      }

      // Reconnect after network switch
      await this.connectWallet();

      return { success: true };
    } catch (error: any) {
      console.error('Network switch error:', error);
      return {
        success: false,
        error: error.message || 'Failed to switch network'
      };
    }
  }

  /**
   * Get wallet transaction history
   */
  async getWalletTransactions(): Promise<ApiResponse<WalletTransaction[]>> {
    try {
      const response = await apiDefaults.get('/wallet/transactions');
      return response as ApiResponse<WalletTransaction[]>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get wallet transactions',
        message: 'Failed to get wallet transactions'
      };
    }
  }

  /**
   * Sync wallet with backend
   */
  private async syncWalletWithBackend(address: string): Promise<void> {
    try {
      await apiDefaults.post('/wallet/sync', {
        address,
        chainId: this.walletInfo?.chainId,
        balance: this.walletInfo?.balance
      });
    } catch (error) {
      console.error('Failed to sync wallet with backend:', error);
    }
  }

  /**
   * Set up wallet event listeners
   */
  private setupWalletEventListeners(): void {
    if (!this.isWalletAvailable()) return;

    const ethereum = (window as any).ethereum;

    // Account changed
    ethereum.on('accountsChanged', async (accounts: string[]) => {
      if (accounts.length === 0) {
        // User disconnected wallet
        await this.disconnectWallet();
      } else {
        // User switched accounts
        await this.connectWallet();
      }
    });

    // Network changed
    ethereum.on('chainChanged', async (chainId: string) => {
      // Reconnect to get updated network info
      await this.connectWallet();
    });

    // Connection status
    ethereum.on('connect', () => {
      console.log('Wallet connected');
    });

    ethereum.on('disconnect', () => {
      console.log('Wallet disconnected');
      this.disconnectWallet();
    });
  }

  /**
   * Validate wallet address
   */
  isValidAddress(address: string): boolean {
    try {
      return isAddress(address);
    } catch (error) {
      return false;
    }
  }

  /**
   * Format wallet address for display
   */
  formatAddress(address: string, start: number = 6, end: number = 4): string {
    if (!address || address.length < start + end) return address;
    return `${address.slice(0, start)}...${address.slice(-end)}`;
  }

  /**
   * Format balance for display
   */
  formatBalance(balance: string, decimals: number = 4): string {
    const num = parseFloat(balance);
    if (num === 0) return '0';
    if (num < 0.0001) return '< 0.0001';
    return num.toFixed(decimals);
  }

  /**
   * Get supported networks
   */
  getSupportedNetworks(): BlockchainNetwork[] {
    return SUPPORTED_NETWORKS;
  }

  /**
   * Get network by chain ID
   */
  getNetworkByChainId(chainId: number): BlockchainNetwork | undefined {
    return SUPPORTED_NETWORKS.find(n => n.chainId === chainId);
  }

  /**
   * Check if current network is supported
   */
  isNetworkSupported(chainId?: number): boolean {
    const currentChainId = chainId || this.walletInfo?.chainId;
    return currentChainId ? SUPPORTED_NETWORKS.some(n => n.chainId === currentChainId) : false;
  }

  /**
   * Get block explorer URL for transaction
   */
  getTransactionUrl(txHash: string, chainId?: number): string {
    const currentChainId = chainId || this.walletInfo?.chainId;
    const network = SUPPORTED_NETWORKS.find(n => n.chainId === currentChainId);
    return network ? `${network.blockExplorerUrl}/tx/${txHash}` : '';
  }

  /**
   * Get block explorer URL for address
   */
  getAddressUrl(address: string, chainId?: number): string {
    const currentChainId = chainId || this.walletInfo?.chainId;
    const network = SUPPORTED_NETWORKS.find(n => n.chainId === currentChainId);
    return network ? `${network.blockExplorerUrl}/address/${address}` : '';
  }

  /**
   * Sign message with wallet
   */
  async signMessage(message: string): Promise<{ success: boolean; signature?: string; error?: string }> {
    try {
      if (!this.provider || !this.walletInfo?.isConnected) {
        return { success: false, error: 'Wallet not connected' };
      }

      const signer = await this.provider.getSigner();
      const signature = await signer.signMessage(message);

      return { success: true, signature };
    } catch (error: any) {
      console.error('Message signing error:', error);
      return {
        success: false,
        error: error.message || 'Failed to sign message'
      };
    }
  }

  /**
   * Check if wallet has sufficient balance for transaction
   */
  async hasSufficientBalance(amount: string): Promise<boolean> {
    try {
      if (!this.walletInfo?.balance) return false;
      
      const balance = parseFloat(this.walletInfo.balance);
      const required = parseFloat(amount);
      
      return balance >= required;
    } catch (error) {
      console.error('Balance check error:', error);
      return false;
    }
  }
}

export const walletService = new WalletService();
export default walletService;
